import {SEARCH_DELAY_IN_MS} from '@hconnect/common/consts'
import {UpmEntityType, TreeNode, KpiWithValue, Kpi} from '@hconnect/common/types'
import {Loader} from '@hconnect/uikit/src/lib2'
import Grid from '@mui/material/Unstable_Grid2'
import useDebounce from '@react-hook/debounce'
import {AxiosError} from 'axios'
import React, {startTransition, useMemo, useState} from 'react'
import {useSearchParams} from 'react-router'

import {KPI_PARAM_NAME} from '../consts'
import {useKpiData, useSensors, useFunctions} from '../hooks'

import {KpiCalculationDetails} from './kpi'
import {KpiCreate} from './KpiCreate'
import {PrePopulatedKpiContainer} from './KpiList'

type KpiCalculationSettingsProps = {
  selectedItem: TreeNode
}

const getShouldShowList = (isCreateMode: boolean, selectedKpi?: KpiWithValue) =>
  !isCreateMode && !selectedKpi
const getIsEditMode = (
  isCreateMode: boolean,
  selectedKpi?: KpiWithValue
): selectedKpi is KpiWithValue => !isCreateMode && !!selectedKpi

const getIsLoading = (
  isRefetchingKpis: boolean,
  isLoadingKpis: boolean,
  isSensorsLoading: boolean,
  isFunctionsLoading: boolean
) => isRefetchingKpis || isLoadingKpis || isSensorsLoading || isFunctionsLoading

export const KpiCalculationSettings: React.FC<KpiCalculationSettingsProps> = ({selectedItem}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreateMode, setIsCreateMode] = useState<boolean>(false)
  const [isRefetchingKpis, setIsRefetchingKpis] = useState<boolean>(false)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DELAY_IN_MS)
  const isDepartment = selectedItem.itemType === UpmEntityType.Department
  const isArea = selectedItem.itemType === UpmEntityType.Area
  const {data, isLoading, isFetching, error, refetch} = useKpiData(
    isDepartment || isArea,
    debouncedSearchTerm,
    isArea
  )

  const [searchParams, setSearchParam] = useSearchParams()
  const tagname = searchParams.get(KPI_PARAM_NAME)

  const {data: sensors = [], isLoading: isSensorsLoading} = useSensors(
    tagname !== null || isCreateMode
  )
  const {data: functions, isLoading: isFunctionsLoading} = useFunctions(
    tagname !== null || isCreateMode
  )
  const isLoadingData = getIsLoading(
    isRefetchingKpis,
    isLoading,
    isSensorsLoading,
    isFunctionsLoading
  )

  const selectedKpi: KpiWithValue | undefined = useMemo(() => {
    if (tagname !== null) {
      for (const processStage of data?.processStages || []) {
        const matchedKpi = processStage.definitionsWithResults.find(
          (kpi) => kpi.definition.tagname === tagname
        )
        if (matchedKpi) return matchedKpi
      }
    }
    return undefined
  }, [data?.processStages, tagname])

  const functionsVariables = useMemo(() => {
    return functions
      ? functions.functionDefinitions.map(({name, description}) => ({name, description}))
      : []
  }, [functions])

  const onCreateSuccess = ({tagname}: Kpi) => {
    startTransition(() => {
      setSearchParam({[KPI_PARAM_NAME]: tagname})
    })
    void refetch().finally(() => setIsRefetchingKpis(false))
    setIsRefetchingKpis(true)
    setIsCreateMode(false)
  }

  if (isLoadingData) {
    return <Loader />
  }

  return (
    <Grid container spacing={2} mt={selectedKpi ? 1 : 0}>
      {getShouldShowList(isCreateMode, selectedKpi) && (
        <PrePopulatedKpiContainer
          data={data?.processStages || []}
          isLoading={isLoadingData}
          error={error as AxiosError}
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value)
            setDebouncedSearchTerm(value)
          }}
          enableCreationMode={() => setIsCreateMode(true)}
        />
      )}
      {isCreateMode && (
        <Grid xs={12} pt={0}>
          <KpiCreate
            kpis={data}
            onCreateSuccess={onCreateSuccess}
            sensors={sensors}
            functionsVariables={functionsVariables}
            functionDefinitions={functions?.functionDefinitions}
            handleClose={() => setIsCreateMode(false)}
          />
        </Grid>
      )}
      {getIsEditMode(isCreateMode, selectedKpi) && (
        <KpiCalculationDetails
          selectedKpi={selectedKpi}
          functions={functions}
          sensors={sensors}
          functionsVariables={functionsVariables}
          isFetchingKpis={isFetching}
        />
      )}
    </Grid>
  )
}
